import React from 'react';

// Typography Component
export const Typography = ({ variant = 'body1', children, className = '', sx = {}, ...props }) => {
  const variantStyles = {
    h1: 'text-4xl font-bold',
    h2: 'text-3xl font-bold',
    h3: 'text-2xl font-bold',
    h4: 'text-xl font-bold',
    h5: 'text-lg font-bold',
    h6: 'text-base font-bold',
    subtitle1: 'text-lg',
    subtitle2: 'text-base',
    body1: 'text-base',
    body2: 'text-sm',
    caption: 'text-xs',
    button: 'text-sm font-medium',
    overline: 'text-xs uppercase tracking-wider',
  };

  const Component = variant.startsWith('h') ? variant : 'p';

  return (
    <Component className={`${variantStyles[variant]} ${className}`} style={sx} {...props}>
      {children}
    </Component>
  );
};

// Box Component
export const Box = ({ children, className = '', sx = {}, ...props }) => (
  <div className={className} style={sx} {...props}>{children}</div>
);

// Button Component
export const Button = ({ 
  variant = 'text',
  color = 'primary',
  size = 'medium',
  startIcon,
  endIcon,
  children,
  onClick,
  className = '',
  sx = {},
  ...props
}) => {
  const baseStyles = 'font-medium transition-all duration-200 flex items-center justify-center';
  const sizeStyles = {
    small: 'text-xs py-1 px-3 rounded',
    medium: 'text-sm py-2 px-4 rounded-md',
    large: 'text-base py-2.5 px-5 rounded-lg',
  };
  const variantStyles = {
    text: {
      primary: 'text-blue-600 hover:bg-blue-50',
      secondary: 'text-purple-600 hover:bg-purple-50',
      success: 'text-green-600 hover:bg-green-50',
      error: 'text-red-600 hover:bg-red-50',
    },
    outlined: {
      primary: 'border border-blue-600 text-blue-600 hover:bg-blue-50',
      secondary: 'border border-purple-600 text-purple-600 hover:bg-purple-50',
      success: 'border border-green-600 text-green-600 hover:bg-green-50',
      error: 'border border-red-600 text-red-600 hover:bg-red-50',
    },
    contained: {
      primary: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow',
      secondary: 'bg-purple-600 hover:bg-purple-700 text-white shadow-sm hover:shadow',
      success: 'bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow',
      error: 'bg-red-600 hover:bg-red-700 text-white shadow-sm hover:shadow',
    },
  };
  
  return (
    <button
      onClick={onClick}
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant][color]} ${className}`}
      style={sx}
      {...props}
    >
      {startIcon && <span className="mr-2">{startIcon}</span>}
      {children}
      {endIcon && <span className="ml-2">{endIcon}</span>}
    </button>
  );
};

// Card Component
export const Card = ({ children, className = '', backgroundColor = 'white', borderColor = '#e5e7eb', sx = {}, ...props }) => (
  <div 
    className={`rounded-xl border overflow-hidden ${className}`}
    style={{ backgroundColor, borderColor, ...sx }}
    {...props}
  >
    {children}
  </div>
);

// Avatar Component
export const Avatar = ({
  initials = '',
  size = 'md',
  backgroundColor = 'blue',
  textColor = 'white',
  className = '',
  sx = {},
  ...props
}) => {
  const sizeStyles = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
  };
  
  const colorMap = {
    blue: 'bg-blue-600 text-white',
    green: 'bg-green-600 text-white',
    red: 'bg-red-600 text-white',
    orange: 'bg-orange-500 text-white',
    purple: 'bg-purple-600 text-white',
    gray: 'bg-gray-600 text-white',
  };
  
  return (
    <div 
      className={`flex items-center justify-center rounded-full font-medium shadow ${sizeStyles[size]} ${colorMap[backgroundColor]} ${className}`}
      style={sx}
      {...props}
    >
      {initials}
    </div>
  );
};

// Chip Component
export const Chip = ({
  label,
  icon,
  color = 'default',
  variant = 'filled',
  size = 'medium',
  className = '',
  sx = {},
  ...props
}) => {
  const baseStyles = 'inline-flex items-center rounded-full';
  const sizeStyles = {
    small: 'text-xs px-2 py-0.5',
    medium: 'text-sm px-2.5 py-1',
    large: 'text-base px-3 py-1.5',
  };
  const variantColorStyles = {
    filled: {
      default: 'bg-gray-100 text-gray-800',
      primary: 'bg-blue-100 text-blue-800',
      secondary: 'bg-purple-100 text-purple-800',
      success: 'bg-green-100 text-green-800',
      error: 'bg-red-100 text-red-800',
      warning: 'bg-yellow-100 text-yellow-800',
      info: 'bg-sky-100 text-sky-800',
    },
    subtle: {
      default: 'bg-gray-50 text-gray-600',
      primary: 'bg-blue-50 text-blue-700',
      secondary: 'bg-purple-50 text-purple-700',
      success: 'bg-green-50 text-green-700',
      error: 'bg-red-50 text-red-700',
      warning: 'bg-amber-50 text-amber-700',
      info: 'bg-sky-50 text-sky-700',
    },
  };
  
  return (
    <span
      className={`${baseStyles} ${sizeStyles[size]} ${variantColorStyles[variant][color]} ${className}`}
      style={sx}
      {...props}
    >
      {icon && <span className="mr-1">{icon}</span>}
      {label}
    </span>
  );
};

// Badge Component
export const Badge = ({ color = 'primary', className = '', sx = {}, ...props }) => {
  const colorStyles = {
    primary: 'bg-blue-600',
    secondary: 'bg-purple-600',
    success: 'bg-green-600',
    error: 'bg-red-600',
    warning: 'bg-amber-500',
    info: 'bg-sky-500',
    orange: 'bg-orange-500',
    green: 'bg-green-500',
  };
  
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${colorStyles[color]} ${className}`}
      style={sx}
      {...props}
    />
  );
};

// Modal Component
export const Modal = ({ open, onClose, children, ...props }) => {
  if (!open) return null;
  
  return (
    <div {...props}>
      {children}
    </div>
  );
};

// Dialog Component
export const Dialog = ({ 
  open, 
  onClose, 
  children, 
  maxWidth = 'sm',
  fullWidth = false,
  PaperProps = {},
  ...props 
}) => {
  if (!open) return null;
  
  const maxWidthClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      {...props}
    >
      <div 
        className={`bg-white rounded-lg shadow-xl overflow-hidden ${maxWidthClasses[maxWidth]} ${fullWidth ? 'w-full mx-4' : ''}`}
        style={PaperProps.sx || {}}
      >
        {children}
      </div>
    </div>
  );
};

// DialogContent Component
export const DialogContent = ({ children, className = '', sx = {}, ...props }) => (
  <div className={`p-6 ${className}`} style={sx} {...props}>{children}</div>
);

// DialogActions Component
export const DialogActions = ({ children, className = '', sx = {}, ...props }) => (
  <div className={`px-6 py-4 flex justify-end gap-2 ${className}`} style={sx} {...props}>{children}</div>
);

export default {
  Typography,
  Box,
  Button,
  Card,
  Avatar,
  Chip,
  Badge,
  Modal,
  Dialog,
  DialogContent,
  DialogActions
};